import {
  filterJoinsByAllowedEntities,
  getJoinsFromSearchEntity,
} from "./searchDefaults";
import { validateSearch } from "./searchSavedNodes";
import {
  getAdvancedFieldsForSubmit,
  getAdvancedNodeObjectForSubmit,
} from "./searchRequestNodes";
import {
  SearchQuery,
  SearchRequestProperties,
  SearchSelection,
} from "./advancedSearch.types";

export interface SearchRequestOptions {
  /** Entities a non-administrator may search; null allows every one. */
  allowedEntityTypes?: Record<string, string> | null;
  scope?: SearchRequestProperties;
  page?: number;
  pageSize?: number;
}

// The legacy filter list carries the searched entity under this key.
const entityFilters = (entityName: string) => [
  { Key: "advancedSearchEntityName", Value: entityName },
];

export function getSearchJoins(
  entityName: string,
  allowedEntityTypes?: Record<string, string> | null
) {
  return filterJoinsByAllowedEntities(
    getJoinsFromSearchEntity(entityFilters(entityName)),
    allowedEntityTypes ?? null
  );
}

/** Field name to message, for what is missing before the search can run. */
export function validateQuery(query: SearchQuery): Record<string, string> {
  return validateSearch(
    entityFilters(query.entityName),
    query.nodes
  ) as unknown as Record<string, string>;
}

export function buildSearchRequest(
  query: SearchQuery,
  { allowedEntityTypes, scope, page, pageSize }: SearchRequestOptions = {}
): Record<string, unknown> {
  const { entityName, nodes, columns, order } = query;

  return {
    ...scope,
    EntityName: entityName,
    Fields: getAdvancedFieldsForSubmit(
      { isAdvancedSearch: true },
      columns,
      entityFilters(entityName)
    ),
    Filters: {
      advancedSearchEntityName: entityName.toLowerCase(),
      fieldOrderedBy: order.by,
      fieldOrderedByAsc: order.asc,
      fieldOrderedBySecondary: order.secondaryBy,
      fieldOrderedBySecondaryAsc: order.secondaryAsc,
      activeStatus: true,
    },
    IsAdvancedSearch: true,
    AdvancedSearchJoins: getSearchJoins(entityName, allowedEntityTypes),
    AdvancedSearchNodes: nodes.map(getAdvancedNodeObjectForSubmit),
    PageRequested: page,
    PageSize: pageSize,
  };
}

/** The ticked records, or null so the server applies the search to every match. */
export const getSelectedIds = (selection: SearchSelection) =>
  selection.allPages ? null : selection.ids;

/** The search as submitted, restricted to the selection - for an export or list change. */
export function buildSelectionRequest(
  query: SearchQuery,
  selection: SearchSelection,
  options: SearchRequestOptions = {}
): Record<string, unknown> {
  return {
    ...buildSearchRequest(query, { ...options, page: undefined, pageSize: undefined }),
    SelectedRecordIds: getSelectedIds(selection),
  };
}

export function buildMarketingListChangeRequest(
  request: ReturnType<typeof buildSelectionRequest>,
  listId: string,
  remove: boolean
) {
  return {
    ...request,
    MarketingListId: listId,
    RemoveFromMarketingList: remove,
  };
}

/** Adds or removes members: which contacts to search, relative to the list. */
export function marketingListScope(listId: string, remove: boolean) {
  return {
    MarketingListScope: {
      ListId: listId,
      Membership: remove ? "OnlyMembers" : "ExcludeMembers",
    },
  };
}
